import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, User, Briefcase, Mail, FileText } from 'lucide-react';

interface RelatedItem {
    title: string;
    description?: string;
    url: string;
    icon?: React.ElementType;
}

interface RelatedContentProps {
    title?: string;
    items?: RelatedItem[];
    className?: string;
}

const RelatedContent: React.FC<RelatedContentProps> = ({
    title,
    items,
    className = ""
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const currentLang = location.pathname.startsWith('/en') ? 'en' : 'de';

    // Default items if none provided
    const defaultItems: RelatedItem[] = [
        {
            title: currentLang === 'de' ? 'Über Chris' : 'About Chris',
            description: currentLang === 'de' ? 'Lernen Sie Ihren Berater kennen' : 'Meet your advisor',
            url: `/${currentLang}/about`,
            icon: User
        },
        {
            title: currentLang === 'de' ? 'Dienstleistungen' : 'Services',
            description: currentLang === 'de' ? 'Was wir für Sie tun können' : 'What we can do for you',
            url: `/${currentLang}/services`,
            icon: Briefcase
        },
        {
            title: currentLang === 'de' ? 'Kontakt' : 'Contact',
            description: currentLang === 'de' ? 'Starten Sie Ihre Reise' : 'Start your journey',
            url: `/${currentLang}/contact`,
            icon: Mail
        }
    ];

    const displayItems = items || defaultItems;
    const displayTitle = title || (currentLang === 'de' ? 'Das könnte Sie auch interessieren' : 'You Might Also Like');

    return (
        <section className={`py-12 bg-slate-50 border-t border-slate-200 ${className}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-8 text-center">{displayTitle}</h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {displayItems.map((item, index) => {
                        const Icon = item.icon || FileText;

                        return (
                            <div
                                key={index}
                                onClick={() => navigate(item.url)}
                                className="group bg-white p-6 shadow-sm border border-slate-100 hover:shadow-md hover:border-accent-main/30 transition-all cursor-pointer flex flex-col items-center text-center"
                            >
                                <div className="w-12 h-12 bg-accent-main/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                    <Icon className="w-6 h-6 text-accent-main" />
                                </div>

                                <h4 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-accent-main transition-colors">
                                    {item.title}
                                </h4>

                                {item.description && (
                                    <p className="text-slate-500 text-sm mb-4">
                                        {item.description}
                                    </p>
                                )}

                                <div className="mt-auto flex items-center text-sm font-medium text-accent-main opacity-0 group-hover:opacity-100 transition-opacity transform translate-y-2 group-hover:translate-y-0">
                                    {currentLang === 'de' ? 'Mehr erfahren' : 'Learn more'} <ArrowRight className="w-4 h-4 ml-1" />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default RelatedContent;
